import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { useAuth } from "../auth/AuthProvider.js";
import { Button, Card, DataTable, Field } from "../components/index.js";
import type { Column } from "../components/index.js";
import {
  formatPeriodLabel,
  periodFromDate,
  recentPeriods
} from "../lib/report-period.js";
import type { Report } from "../types.js";

/**
 * Relatório (Design.md §8.4). Lista os últimos relatórios (filtráveis por
 * período) e — para ADMIN — gera e baixa o PDF mensal. O PDF é renderizado no
 * backend (snapshot imutável no GCS); o download é um proxy autenticado.
 */
const NUM = new Intl.NumberFormat("pt-BR");

export function RelatorioPage(): ReactNode {
  const { api, me } = useAuth();
  const canGenerate = me?.role === "ADMIN";
  const now = useMemo(() => new Date(), []);
  const periodOptions = useMemo(() => recentPeriods(now, 12), [now]);

  const [reports, setReports] = useState<Report[]>([]);
  const [filterPeriod, setFilterPeriod] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const reqSeq = useRef(0);

  const [genPeriod, setGenPeriod] = useState<string>(periodFromDate(now));
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setErro(null);
    try {
      const res = await api.listReports(
        filterPeriod.length > 0 ? filterPeriod : undefined
      );
      if (seq !== reqSeq.current) return;
      setReports(res.reports);
    } catch (error: unknown) {
      if (seq !== reqSeq.current) return;
      setErro(
        error instanceof Error ? error.message : "Falha ao carregar os relatórios."
      );
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [api, filterPeriod]);

  useEffect(() => {
    void load();
  }, [load]);

  const downloadReport = useCallback(
    async (id: string) => {
      setDownloadingId(id);
      try {
        const blob = await api.downloadReport(id);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        // Libera o object URL após a aba abrir o PDF.
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error: unknown) {
        setErro(
          error instanceof Error ? error.message : "Falha ao baixar o relatório."
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [api]
  );

  async function handleGenerate(): Promise<void> {
    setGenError(null);
    setGenerating(true);
    try {
      const res = await api.generateReport(genPeriod);
      await load();
      await downloadReport(res.report.id);
    } catch (error: unknown) {
      setGenError(
        error instanceof Error ? error.message : "Falha ao gerar o relatório."
      );
    } finally {
      setGenerating(false);
    }
  }

  const columns: readonly Column<Report>[] = [
    { key: "period", header: "Período", render: (r) => formatPeriodLabel(r.period) },
    {
      key: "generatedAt",
      header: "Gerado em",
      render: (r) => new Date(r.generatedAt).toLocaleString("pt-BR")
    },
    {
      key: "rows",
      header: "Linhas",
      align: "right",
      render: (r) => NUM.format(r.rowCount)
    },
    {
      key: "units",
      header: "Unidades",
      align: "right",
      render: (r) => NUM.format(r.totalUnits)
    },
    {
      key: "actions",
      header: "Ações",
      align: "right",
      render: (r) => (
        <Button
          variant="secondary"
          disabled={downloadingId === r.id}
          onClick={() => void downloadReport(r.id)}
        >
          {downloadingId === r.id ? "Baixando…" : "Baixar PDF"}
        </Button>
      )
    }
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <header>
        <h2 style={{ fontSize: 21, fontWeight: 700 }}>Relatório</h2>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
          Relatórios mensais de envios · PDF
        </p>
      </header>

      {canGenerate ? (
        <Card>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "0.85rem",
              flexWrap: "wrap"
            }}
          >
            <Field label="Mês do relatório" htmlFor="gen-period" style={{ minWidth: 200 }}>
              <select
                id="gen-period"
                value={genPeriod}
                onChange={(e) => setGenPeriod(e.target.value)}
              >
                {periodOptions.map((p) => (
                  <option key={p} value={p}>
                    {formatPeriodLabel(p)}
                  </option>
                ))}
              </select>
            </Field>
            <Button disabled={generating} onClick={() => void handleGenerate()}>
              {generating ? "Gerando…" : "Gerar e baixar"}
            </Button>
          </div>
          {genError !== null ? (
            <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, marginTop: "0.5rem" }}>
              {genError}
            </p>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "0.85rem",
            marginBottom: "0.85rem",
            flexWrap: "wrap"
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 700 }}>Relatórios gerados</h3>
          <Field label="Filtrar por período" htmlFor="filter-period" style={{ minWidth: 200 }}>
            <select
              id="filter-period"
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
            >
              <option value="">Todos os períodos</option>
              {periodOptions.map((p) => (
                <option key={p} value={p}>
                  {formatPeriodLabel(p)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {erro !== null ? (
          <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, marginBottom: "0.5rem" }}>
            {erro}
          </p>
        ) : null}

        <DataTable
          columns={columns}
          rows={reports}
          getRowKey={(r) => r.id}
          empty={loading ? "Carregando…" : "Nenhum relatório gerado."}
        />
      </Card>
    </div>
  );
}
